// src/utils/deliveryDateUtils.js
const HOLIDAY_CACHE = new Map();

const isWeekend = (date) => [0, 6].includes(date.getDay());

const pad2 = (value) => String(value).padStart(2, '0');
const formatLocalDateKey = (date) =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const createDateFromParts = (year, month, day) => new Date(year, month - 1, day, 12, 0, 0, 0);

const calculateEasterSunday = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    return createDateFromParts(year, month, day);
};

const getEcuadorHolidaySet = (year) => {
    if (HOLIDAY_CACHE.has(year)) {
        return HOLIDAY_CACHE.get(year);
    }

    const easterSunday = calculateEasterSunday(year);
    const carnivalMonday = new Date(easterSunday);
    carnivalMonday.setDate(easterSunday.getDate() - 48);

    const carnivalTuesday = new Date(easterSunday);
    carnivalTuesday.setDate(easterSunday.getDate() - 47);

    const goodFriday = new Date(easterSunday);
    goodFriday.setDate(easterSunday.getDate() - 2);

    const holidays = [
        createDateFromParts(year, 1, 1),
        carnivalMonday,
        carnivalTuesday,
        goodFriday,
        createDateFromParts(year, 5, 1),
        createDateFromParts(year, 5, 24),
        createDateFromParts(year, 8, 10),
        createDateFromParts(year, 10, 9),
        createDateFromParts(year, 11, 2),
        createDateFromParts(year, 11, 3),
        createDateFromParts(year, 12, 25),
    ];

    const holidaySet = new Set(holidays.map((holidayDate) => formatLocalDateKey(holidayDate)));
    HOLIDAY_CACHE.set(year, holidaySet);

    return holidaySet;
};

const isHoliday = (date) => getEcuadorHolidaySet(date.getFullYear()).has(formatLocalDateKey(date));

const isLaborDay = (date) => !isWeekend(date) && !isHoliday(date);

// Add labor hours
export const addLaborHours = (startDate, hours) => {
    const result = new Date(startDate);
    let remainingHours = hours;
    let firstLaborDayKey = null;
    const shouldCountFirstLaborDay = isLaborDay(startDate) && startDate.getHours() < 12;

    while (remainingHours > 0) {
        result.setHours(result.getHours() + 1);
        if (isLaborDay(result)) {
            const currentDayKey = formatLocalDateKey(result);

            // First labor day only counts for orders created before noon on a labor day.
            if (!firstLaborDayKey) {
                firstLaborDayKey = currentDayKey;
            }

            if (!shouldCountFirstLaborDay && currentDayKey === firstLaborDayKey) {
                continue;
            }

            remainingHours--;
        }
    }

    return result;
};

// Calculate estimated delivery range
export const getEstimatedDeliveryDate = (tag, orderDate = new Date()) => {
    const hour = orderDate.getHours();
    let minHours;
    let maxHours;

    switch (tag) {
        case 'P':
            minHours = 36;
            maxHours = 60;
            if (hour < 12) {
                minHours -= 12; // next day delivery
                maxHours -= 12; // next day delivery
            }
            break;
        case 'S':
            minHours = 60;
            maxHours = 84;
            if (hour < 12) {
                minHours -= 12;
                maxHours -= 12;
            }
            break;
        case 'E':
        case 'O':
            minHours = 84;
            maxHours = 108;
            if (hour < 12) {
                minHours -= 24;
                maxHours -= 24;
            }
            break;
        default:
            minHours = 108;
            maxHours = 132;
            if (hour < 12) {
                minHours -= 24;
                maxHours -= 24;
            }
    }

    const minDate = addLaborHours(orderDate, minHours);
    const maxDate = addLaborHours(orderDate, maxHours);

    return {
        min: minDate,
        max: maxDate,
    };
};
